import { CreditCard, User as UserIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { useSubscriptionStatus } from '@/api/subscription'
import { PaymentMethodCard } from '@/components/manager/subscription/PaymentMethodCard'
import { ChangePlanCard } from '@/components/manager/subscription/ChangePlanCard'
import { InvoicesTable } from '@/components/manager/subscription/InvoicesTable'
import { CancelSubscriptionCard } from '@/components/manager/subscription/CancelSubscriptionCard'
import { ActivateNowCard } from '@/components/manager/subscription/ActivateNowCard'
import { MySubscriptionCard } from '@/components/manager/subscription/MySubscriptionCard'
import { ChangePlanView } from '@/components/manager/subscription/ChangePlanView'

/**
 * Onglet Facturation du manager.
 *
 * 2 modes :
 *  - Mode 1 (par défaut) : récap de l'abonnement actuel + cartes annexes
 *    (paiement, factures, annulation). Pas de grille de plans.
 *  - Mode 2 (?action=change-plan) : page dédiée pour changer de plan
 *    (layout 2 colonnes : configure / plans avec tabs Indé/Équipes).
 *
 * Source de vérité billing : /api/billing/summary/ (calcul backend).
 */
export function BillingView() {
  const { t } = useTranslation()
  const darkMode = useUIStore((s) => s.darkMode)
  const navigate = useNavigate()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const [searchParams] = useSearchParams()

  const { data: subData } = useSubscriptionStatus()
  const subscription = subData?.subscription ?? null

  const isChangePlanMode = searchParams.get('action') === 'change-plan'

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header tabs (Profil / Facturation) — inchangé */}
      <div
        className={cn(
          'flex-shrink-0 border-b',
          darkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white',
        )}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-1" role="tablist" aria-label="Billing sections">
            <button
              type="button"
              role="tab"
              aria-selected="false"
              onClick={() => navigate(`/${locale}/manager?view=profile`)}
              className={cn(
                'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors border-transparent',
                darkMode
                  ? 'text-slate-400 hover:text-white hover:border-slate-700'
                  : 'text-slate-500 hover:text-slate-900 hover:border-slate-300',
              )}
            >
              <UserIcon className="h-4 w-4" />
              {t('profile.tabs.profile', 'Profil')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected="true"
              className={cn(
                'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
                darkMode
                  ? 'border-white text-white'
                  : 'border-[#0A192F] text-[#0A192F]',
              )}
            >
              <CreditCard className="h-4 w-4" />
              {t('profile.tabs.billing', 'Facturation')}
            </button>
          </nav>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 lg:px-8 py-6 max-w-4xl mx-auto space-y-6">
          <header className="space-y-1">
            <div className="flex items-center gap-2">
              <CreditCard
                className={cn('h-5 w-5', darkMode ? 'text-white' : 'text-[#0A192F]')}
              />
              <h1
                className={cn(
                  'text-2xl font-semibold',
                  darkMode ? 'text-white' : 'text-slate-900',
                )}
              >
                {t('manager.billing.title', 'Facturation')}
              </h1>
            </div>
            <p
              className={cn(
                'text-sm',
                darkMode ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              {t(
                'manager.billing.subtitle',
                'Gérez votre plan, votre carte bancaire et téléchargez vos factures.',
              )}
            </p>
          </header>

          {isChangePlanMode ? (
            <ChangePlanView />
          ) : (
            <>
              {/* Récap "Mon abonnement" (lit /api/billing/summary/) */}
              <MySubscriptionCard />

              {/* CTA activate-now si trialing */}
              {subscription?.status === 'trialing' && (
                <ActivateNowCard
                  subscription={subscription}
                  darkMode={darkMode}
                />
              )}

              {/* CB et factures : toujours visibles dès qu'il y a un Customer
                  Stripe (peu importe l'état de la sub). C'est nécessaire pour
                  qu'un user en sub canceled puisse MAJ sa CB avant de re-souscrire. */}
              <PaymentMethodCard darkMode={darkMode} />
              <InvoicesTable darkMode={darkMode} />

              {/* Change de plan / cancel : conditionnés à une sub active */}
              {subscription && (
                <>
                  {(subscription.status === 'active' ||
                    subscription.status === 'trialing') &&
                    !subscription.cancel_at_period_end && (
                      <ChangePlanCard
                        subscription={subscription}
                        darkMode={darkMode}
                      />
                    )}

                  {(subscription.status === 'active' ||
                    subscription.status === 'trialing' ||
                    subscription.status === 'past_due') && (
                    <CancelSubscriptionCard
                      subscription={subscription}
                      darkMode={darkMode}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
